/**
 * 页面嵌入模式的识别工具。
 *
 * 这里的「嵌入」指管理后台页面被放进 iframe 展示、需要隐藏站点自己的顶栏和侧栏，
 * 有两种来源：
 * 1. URL 带 embed 参数的通用嵌入链接；
 * 2. TodayWork 桌面客户端（AI PC 版）用 iframe 嵌入。
 *
 * 注意区分两个概念：
 * - AI 资料员的工作区（src/pages/aiClerk/workspace）是业务页面外壳，和嵌入无关；
 * - desktopLib.isDesktop 指今日相机客户端，不是 TodayWork 客户端，见 utils/clientAttribution。
 */
import { isTodayWorkClient } from './clientAttribution'

/**
 * 当前页面是否被 TodayWork 桌面客户端用 iframe 嵌入。
 *
 * 业务路由跳转不会保留入口的 embed 参数。桌面 preload 在每次加载前写入
 * 客户端标记，结合 iframe 身份识别，详情、返回和刷新都能保持嵌入布局。
 * 仅存储标记不足以隐藏导航：新浏览器窗口可能继承 opener 的 sessionStorage。
 *
 * @returns 在 TodayWork 客户端的 iframe 内为 true，其余情况为 false
 *
 * @example
 * ```ts
 * isEmbeddedInTodayWork() // TodayWork 客户端的 iframe 内 true；浏览器直接打开 false
 * ```
 */
export function isEmbeddedInTodayWork(): boolean {
  return window.self !== window.top && isTodayWorkClient()
}

/**
 * 当前页面是否处于嵌入模式（需要隐藏站点顶栏和侧栏）。
 *
 * @param search - URL 的 query 串，默认取 window.location.search
 * @returns URL 带 embed 参数，或被 TodayWork 客户端嵌入时为 true
 *
 * @example
 * ```ts
 * isEmbedMode('?embed=1') // true
 * isEmbedMode('')         // 仅在 TodayWork 客户端的 iframe 内为 true
 * ```
 */
export function isEmbedMode(search = window.location.search): boolean {
  return new URLSearchParams(search).has('embed') || isEmbeddedInTodayWork()
}
